import { Suspense, useEffect, useState } from 'react';
import { SidePanelContent } from '~/components/sidepanel-section';
import { P2GText, P2GContainer } from '@parcel2go/core-ui';
import { Customer, Dimensions } from '~/lib/api/smartsend/types';
import { RuleApiModel } from '~/lib/api/rulemanager/types';
import { useRuleManagerApi } from '~/lib/api/rulemanager/use-rulemanager-api';
import { RuleNames } from '~/apps/rulemanager/lib/helpers/RuleGenerator';
import { Actions } from '~/apps/rulemanager/lib/helpers/Actions';
import { EstimatedTimeSavingScreen } from './estimated-time-saving-screen';
import SmartSendOnboardingServiceRules from './service-rules';
import SmartSendOnboardingDimensionsRules from './dimension-rules';
import { RuleInserPosition } from '~/apps/rulemanager/rulemanager-core';
import Loading from '~/components/loading/Loading';
import { OnboardingTitle } from './onboarding-title';
import { OnboardingActionBar } from '../intro/onboarding-action-bar';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import useContentString from '~/hooks/use-content-string';

type props = {
  customer?: Customer;
  isXs: boolean;
  onDone: (added: number, meta: { [key: string]: any }) => void;
  onSkip: () => void;
  numberOfParcels: number;
};

const RulesScreen = ({ isXs, onDone, customer, onSkip, numberOfParcels }: props) => {
  const { getRules, createRule, updatePriority } = useRuleManagerApi();
  const [rules, setRules] = useState<RuleApiModel[]>([]);
  const [loadingRules, setLoadingRules] = useState(true);
  const [serviceFromRules, setServiceFromRules] = useState<string | null>();
  const [protectionFromRules, setProtectionFromRules] = useState(false);
  const [dimensionsFromRules, setDimensionsFromRules] = useState<Dimensions | null>();
  const [rulesTheCustomerWantsToDo, setRulesTheCustomerWantsToDo] = useState<string[]>(null);
  const [rulesTheCustomerDid, setRulesTheCustomerDid] = useState<string[]>([]);
  const speed = useContentString('smartsend-setup-connection-speed-stats-string');

  useEffect(() => {
    const loadRules = async () => {
      setLoadingRules(true);

      const response = await getRules();
      if (response.success) {
        // Service
        const serviceRule = response.result.find((r) => r.name === RuleNames.DomesticService);
        if (serviceRule) {
          setServiceFromRules(serviceRule?.actions.find((a) => a.field === Actions.Item_Service)?.value);
        }

        // Dimensions
        const dimsToUse: Dimensions = {};
        const dimensionRule = response.result.find((r) => r.name === RuleNames.Dimensions);
        if (dimensionRule) {
          const height = dimensionRule.actions.find((x) => x.field === Actions.Item_Height)?.value;
          const width = dimensionRule.actions.find((x) => x.field === Actions.Item_Width)?.value;
          const length = dimensionRule.actions.find((x) => x.field === Actions.Item_Length)?.value;
          if (height && width && length) {
            dimsToUse.Height = Number(height);
            dimsToUse.Width = Number(width);
            dimsToUse.Length = Number(length);
          }
        } else {
          dimsToUse.Height = 45;
          dimsToUse.Width = 35;
          dimsToUse.Length = 16;
        }

        // Weight
        const weightRule = response.result.find((r) => r.name === RuleNames.Weight);
        if (weightRule) {
          const weightAction = weightRule.actions.find((x) => x.field === Actions.Item_Weight);
          if (weightAction) {
            dimsToUse.Weight = Number(weightAction.value);
          }
        } else {
          dimsToUse.Weight = 1;
        }

        // Protetcion
        const protectionRule = response.result.find((r) => r.name === RuleNames.Extras);
        if (protectionRule) {
          const protectionAction = protectionRule.actions.some(
            (x) =>
              x.value === 'true' && (x.field === Actions.Extra_ExtendedBaseCover || x.field === Actions.Extra_Cover)
          );
          setProtectionFromRules(protectionAction);
        }

        // Set state
        setDimensionsFromRules(dimsToUse);
        setRules(response.result);
      }
      setLoadingRules(false);
    };
    loadRules();
  }, []);

  const storeRules = (rules: RuleApiModel[]) => {
    rules.forEach((r, i) => {
      r.priority = i;
    });
    updatePriority(rules.map((x) => x.id));
    setRules(rules);
  };

  const addRules = async (newRules: RuleApiModel[], position: RuleInserPosition): Promise<boolean> => {
    const rulesToMake = newRules.filter((nr) => !rules.some((r) => r.name === nr.name));
    if (rulesToMake.length > 0) {
      for (const rule of rulesToMake) {
        const r = await createRule(rule);
        if (r.success) {
          rule.id = r.result.id;
        }
      }
      storeRules(position === 'first' ? [...newRules, ...rules] : [...rules, ...newRules]);
    }
    return true;
  };

  useEffect(() => {
    if (rulesTheCustomerWantsToDo !== null && rulesTheCustomerWantsToDo.length === 0) {
      onDone(rulesTheCustomerDid.length, {
        protectionTaken: rulesTheCustomerDid.includes(RuleNames.Extras),
        rulesDone: rulesTheCustomerDid
      });
    }
  }, [rulesTheCustomerWantsToDo, onDone, rulesTheCustomerDid]);

  if (loadingRules) {
    return (
      <>
        <OnboardingScreenContentWrapper>
          <P2GContainer maxWidth="md">
            <SidePanelContent>
              <OnboardingTitle
                text="Set defaults to save you <span>time</span>"
                translationKey="SETUP_RULES_LOADING_TITLE"
              />
              <Loading />
            </SidePanelContent>
          </P2GContainer>
        </OnboardingScreenContentWrapper>
        {speed && (
          <Suspense>
            <OnboardingActionBar>
              <P2GText variant="subtitle2">{speed}</P2GText>
            </OnboardingActionBar>
          </Suspense>
        )}
      </>
    );
  }

  if (rulesTheCustomerWantsToDo === null) {
    return (
      <EstimatedTimeSavingScreen
        numberOfParcels={numberOfParcels}
        isXs={isXs}
        onDone={setRulesTheCustomerWantsToDo}
        onSkip={onSkip}
      />
    );
  }

  if (rulesTheCustomerWantsToDo.length === 0) {
    return null;
  }

  if (
    rulesTheCustomerWantsToDo.includes(RuleNames.Dimensions) ||
    rulesTheCustomerWantsToDo.includes(RuleNames.Weight)
  ) {
    return (
      <SmartSendOnboardingDimensionsRules
        isXs={isXs}
        customer={customer}
        onDone={(added) => {
          setRulesTheCustomerDid((s) => [...s, ...added]);
          setRulesTheCustomerWantsToDo(
            rulesTheCustomerWantsToDo.filter((x) => x !== RuleNames.Dimensions && x !== RuleNames.Weight)
          );
        }}
        addRules={addRules}
        initialDimensions={dimensionsFromRules}
        rulesToInclude={rulesTheCustomerWantsToDo}
      />
    );
  }

  if (rulesTheCustomerWantsToDo.includes(RuleNames.DomesticService)) {
    return (
      <SmartSendOnboardingServiceRules
        isXs={isXs}
        customer={customer}
        onDone={(added) => {
          setRulesTheCustomerDid((s) => [...s, ...added]);
          setRulesTheCustomerWantsToDo(rulesTheCustomerWantsToDo.filter((x) => x !== RuleNames.DomesticService));
        }}
        initialProtection={protectionFromRules}
        addRules={addRules}
        initialService={serviceFromRules}
        initialDimensions={dimensionsFromRules}
      />
    );
  }

  return null;
};

export default RulesScreen;
